import { AuthenticatedContext, User } from '@sec-loans-ui/common/types'
import {
  applicationDataToWizardData,
  getIsWizardDataComplete,
  getWizardData,
  getWizardFlow,
  isShortWizardFlow,
  shouldGoToEndStep,
} from '@sec-loans-ui/ifp-loans/utils'
import { assertValue } from '@shared/helpers'
import {
  IfpApplication,
  IfpApplicationAction,
  IfpApplicationActionResponse,
  IfpApplicationState,
  IfpGetApplicationResponse,
  IfpLoan,
  SecLoansErrorCode,
} from '@shared/sec-loans'
import { ErrorResponse } from '@shared/types'
import { getMachineStorageKey } from '@ui-common/hooks'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep'
import {
  type EditStepEvent as CommonEditStepEvent,
  type NextStepEvent as CommonNextStepEvent,
  type PreviousStepEvent,
} from '@ui-common/types'
import { removeSessionStorageItem, toNumber } from '@ui-common/utils'
import { assign, setup } from 'xstate'

import { IfpBasicInfoFormData } from '../steps/ifp-info/types'
import { signStepMachineId } from '../steps/sign-step/signStepMachine'
import { IfpWizardData } from './types'

export enum WizardStep {
  EntryStep = 'entry',
  GetApplicationStep = 'getApplication',
  StatusStep = 'status',
  IfpInfoStep = 'ifpInfo',
  HouseholdInfoStep = 'householdInfo',
  ApplicantInfoStep = 'applicantInfo',
  CancelApplicationStep = 'cancelFormStep',
  SendStep = 'send',
  ReturnApplicationStep = 'return',
  EndStep = 'end',
}

export enum IfpWizardType {
  Long = 'long',
  Short = 'short',
}

export type EditableStep = WizardStep.IfpInfoStep | WizardStep.HouseholdInfoStep | WizardStep.ApplicantInfoStep

export interface WizardContext extends Partial<AuthenticatedContext> {
  user: User
  applicationId: string
  isPrimaryApplicant?: boolean
  loan: IfpLoan
  isNewApplication: boolean
  wizardData?: IfpWizardData
  isEditing?: boolean
  wizardType?: IfpWizardType
  currentApplicantInfoIndex: number
  isWizardComplete?: boolean
  isFinalizationPhase?: boolean
  actionType?: IfpApplicationAction
  applicationState: IfpApplicationState
  actionsNeeded?: IfpApplication['actionsNeeded']
  shouldShowSentModal?: boolean
  hasSendError?: boolean
  sendError?: ErrorResponse<SecLoansErrorCode>
}

type NextStepEvent = CommonNextStepEvent<WizardContext['wizardData']>
type EditStepEvent = CommonEditStepEvent<EditableStep>
type BackToStatusEvent = { type: 'BACK_TO_STATUS' }
type GetApplicationEvent = GetDataDoneEvent<IfpGetApplicationResponse>
type EditApplicantInfoEvent = { type: 'EDIT_APPLICANT_INFO'; index: number }
type SendSuccessEvent = { type: 'SEND_SUCCESS'; data: IfpApplicationActionResponse }
type SendFailedEvent = { type: 'SEND_FAILED'; error: ErrorResponse<SecLoansErrorCode> | undefined }
type SubmitEvent = { type: 'SUBMIT' }
type SendErrorConfirmedEvent = { type: 'SEND_ERROR_CONFIRMED' }
type SendCancelEvent = { type: 'SEND_CANCELED' }
type CancelApplicationEvent = { type: 'CANCEL_APPLICATION' }
type ReturnApplicationEvent = { type: 'RETURN_APPLICATION' }
type onCancelledApplicationEvent = { type: 'APPLICATION_CANCELLED' }
type onReturnedApplicationEvent = { type: 'APPLICATION_RETURNED' }

type WizardEvent =
  | GetApplicationEvent
  | NextStepEvent
  | PreviousStepEvent
  | EditStepEvent
  | BackToStatusEvent
  | EditApplicantInfoEvent
  | SendSuccessEvent
  | SendFailedEvent
  | SubmitEvent
  | SendErrorConfirmedEvent
  | SendCancelEvent
  | CancelApplicationEvent
  | ReturnApplicationEvent
  | onCancelledApplicationEvent
  | onReturnedApplicationEvent

export type WizardMachineType = ReturnType<typeof createWizardMachine>
type WizardMachineParams = { onCancelledApplication: () => void; onReturnedApplication: () => void }

const saveWizardDataAction = {
  type: 'saveFormData',
  params: ({ event }: { event: NextStepEvent }) => assertValue(event.data, 'event.data'),
} as const

export const wizardMachineId = 'ifpLoansWizardMachine'

export const clearIfpWizardMachineSessionStorage = (): void =>
  removeSessionStorageItem(getMachineStorageKey(wizardMachineId))

const commonGetApplicationActions = [
  {
    type: 'saveFormData',
    params: ({ event }: { event: GetApplicationEvent }) => applicationDataToWizardData(event.data.data ?? {}),
  },
  {
    type: 'saveApplicationMetadata',
    params: ({ event }: { event: GetApplicationEvent }) => event.data,
  },
] as const

// return type is derived from setup fn and used in WizardMachineType
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createWizardMachine = ({ onCancelledApplication, onReturnedApplication }: WizardMachineParams) =>
  setup({
    types: {
      context: {} as WizardContext,
      events: {} as WizardEvent,
      input: {} as WizardContext,
    },
    actions: {
      onCancelledApplication,
      onReturnedApplication,
      enableEdit: assign(() => {
        return { isEditing: true }
      }),
      disableEdit: assign(() => {
        return { isEditing: false }
      }),
      saveFormData: assign(({ context }, wizardData: Partial<IfpWizardData>) => {
        const { loan } = context
        const updatedWizardData = getWizardData(
          {
            ...context.wizardData,
            ...wizardData,
          },
          loan,
        )
        const isWizardComplete = getIsWizardDataComplete({
          data: updatedWizardData,
          loan: context.loan,
        })

        return {
          wizardData: updatedWizardData,
          isWizardComplete,
          // Keep isFinalizationPhase true when wizard has been once completed
          isFinalizationPhase: context.isFinalizationPhase || isWizardComplete,
          wizardType: updatedWizardData.basicInfo
            ? getWizardFlow({
                principalExemptionMonthsLeft: loan.principalExemptionMonthsLeft,
                durationInMonths: toNumber(updatedWizardData.basicInfo.durationInMonths),
                hasFinancialDifficulties: updatedWizardData.basicInfo?.hasFinancialDifficulties,
              })
            : undefined,
        }
      }),
      saveApplicationMetadata: assign(({ context }, data: IfpGetApplicationResponse) => {
        const { user } = context
        return {
          isPrimaryApplicant: data.primaryUuid === user.debtorUuid,
          applicationState: data.state,
          actionsNeeded: data.actionsNeeded,
        }
      }),
      incrementCurrentApplicantInfoIndex: assign(({ context }) => ({
        currentApplicantInfoIndex: context.currentApplicantInfoIndex + 1,
      })),
      decrementCurrentApplicantInfoIndex: assign(({ context }) => ({
        currentApplicantInfoIndex: context.currentApplicantInfoIndex - 1,
      })),
      sortDebtors: assign(({ context }) => ({
        loan: {
          ...context.loan,
          debtors: context.loan.debtors.toSorted((d) => (d.uuid === context.user.debtorUuid ? -1 : 1)),
        },
      })),
      setCurrentApplicantInfoIndex: assign((_, index: number) => ({ currentApplicantInfoIndex: index })),
      resetCurrentApplicantInfoIndex: assign(() => ({ currentApplicantInfoIndex: 0 })),
      saveIfpApplicationState: assign((_, data: IfpApplicationActionResponse) => ({
        applicationState: data.applicationState,
      })),
      setShouldShowSentModal: assign(() => ({
        shouldShowSentModal: true,
      })),
      setSendError: assign((_, error: ErrorResponse<SecLoansErrorCode> | undefined) => {
        return {
          hasSendError: true,
          sendError: error,
        }
      }),
      clearSendError: assign(() => ({ hasSendError: false, sendError: undefined })),
      removeSendError: assign(() => ({ hasSendError: false })),
      removeSigningMachineFromSessionStorage: () => {
        const signMachineStorageId = getMachineStorageKey(signStepMachineId)
        removeSessionStorageItem(signMachineStorageId)
      },
    },
    guards: {
      isEditIfpInfoEvent: (_, step: EditableStep) => step === WizardStep.IfpInfoStep,
      isEditHouseholdInfoEvent: (_, step: EditableStep) => step === WizardStep.HouseholdInfoStep,
      isEditing: ({ context }) => !!context.isEditing,
      shouldGoToShortWizard: ({ context }, { durationInMonths, hasFinancialDifficulties }: IfpBasicInfoFormData) => {
        const { principalExemptionMonthsLeft } = context.loan
        return isShortWizardFlow({
          principalExemptionMonthsLeft,
          durationInMonths: toNumber(durationInMonths),
          hasFinancialDifficulties,
        })
      },
      isShortWizard: ({ context }) => context.wizardType === IfpWizardType.Short,
      shouldGoToNextApplicantInfo: ({ context }) => {
        const { debtors } = assertValue(context.loan, 'context.loan')
        const { currentApplicantInfoIndex } = context
        return currentApplicantInfoIndex + 1 < debtors.length
      },
      shouldGoToPreviousApplicantInfo: ({ context }) => {
        const { currentApplicantInfoIndex } = context
        return currentApplicantInfoIndex > 0
      },
      shouldGoToEndStep,
    },
  }).createMachine({
    id: wizardMachineId,
    initial: WizardStep.EntryStep,
    context: ({ input }) => ({
      ...input,
      currentApplicantInfoIndex: 0,
    }),
    on: {
      BACK_TO_STATUS: { target: `.${WizardStep.StatusStep}` },
    },
    states: {
      [WizardStep.EntryStep]: {
        id: WizardStep.EntryStep,
        entry: {
          type: 'sortDebtors',
        },
        always: [
          {
            guard: ({ context }) => context.isNewApplication,
            target: WizardStep.StatusStep,
          },
          {
            target: WizardStep.GetApplicationStep,
          },
        ],
      },
      [WizardStep.GetApplicationStep]: {
        on: {
          GET_DATA_DONE: [
            {
              guard: {
                type: 'shouldGoToEndStep',
                params: ({ event }: { event: GetApplicationEvent }) => event.data,
              },
              actions: commonGetApplicationActions,
              target: WizardStep.EndStep,
            },
            {
              actions: commonGetApplicationActions,
              target: WizardStep.StatusStep,
            },
          ],
        },
      },
      [WizardStep.StatusStep]: {
        entry: ['disableEdit', 'resetCurrentApplicantInfoIndex'],
        on: {
          NEXT_STEP: { target: WizardStep.IfpInfoStep },
          SUBMIT: { target: WizardStep.SendStep },
          CANCEL_APPLICATION: { target: WizardStep.CancelApplicationStep },
          RETURN_APPLICATION: { target: WizardStep.ReturnApplicationStep },
          EDIT: [
            {
              target: WizardStep.IfpInfoStep,
              guard: { type: 'isEditIfpInfoEvent', params: ({ event }) => event.step },
              actions: {
                type: 'enableEdit',
              },
            },
            {
              target: WizardStep.HouseholdInfoStep,
              guard: { type: 'isEditHouseholdInfoEvent', params: ({ event }) => event.step },
              actions: {
                type: 'enableEdit',
              },
            },
          ],
          EDIT_APPLICANT_INFO: [
            {
              target: WizardStep.ApplicantInfoStep,
              actions: [
                {
                  type: 'setCurrentApplicantInfoIndex',
                  params: ({ event }) => event.index,
                },
                'enableEdit',
              ],
            },
          ],
          SEND_ERROR_CONFIRMED: [
            {
              actions: ['removeSendError'],
            },
          ],
        },
      },
      [WizardStep.IfpInfoStep]: {
        on: {
          PREVIOUS_STEP: [{ guard: 'isEditing', target: WizardStep.StatusStep }],
          NEXT_STEP: [
            {
              target: WizardStep.StatusStep,
              guard: 'isEditing',
              actions: [saveWizardDataAction, 'disableEdit'],
            },
            {
              target: WizardStep.ApplicantInfoStep,
              guard: {
                type: 'shouldGoToShortWizard',
                params: ({ event }) => assertValue(event.data?.basicInfo, 'event.data.basicInfo'),
              },
              actions: [saveWizardDataAction],
            },
            {
              target: WizardStep.HouseholdInfoStep,
              actions: [saveWizardDataAction],
            },
          ],
        },
      },
      [WizardStep.HouseholdInfoStep]: {
        on: {
          PREVIOUS_STEP: [{ guard: 'isEditing', target: WizardStep.StatusStep }, { target: WizardStep.IfpInfoStep }],
          NEXT_STEP: [
            {
              target: WizardStep.StatusStep,
              guard: 'isEditing',
              actions: [saveWizardDataAction],
            },
            {
              target: WizardStep.ApplicantInfoStep,
              actions: {
                type: 'saveFormData',
                params: ({ event }) => assertValue(event.data, 'event.data'),
              },
            },
          ],
        },
      },
      [WizardStep.ApplicantInfoStep]: {
        on: {
          PREVIOUS_STEP: [
            { guard: 'isEditing', target: WizardStep.StatusStep },
            {
              guard: 'shouldGoToPreviousApplicantInfo',
              target: WizardStep.ApplicantInfoStep,
              actions: ['decrementCurrentApplicantInfoIndex'],
            },
            { guard: 'isShortWizard', target: WizardStep.IfpInfoStep },
            { target: WizardStep.HouseholdInfoStep },
          ],
          NEXT_STEP: [
            {
              target: WizardStep.StatusStep,
              guard: 'isEditing',
              actions: [saveWizardDataAction],
            },
            {
              guard: 'shouldGoToNextApplicantInfo',
              actions: [saveWizardDataAction, 'incrementCurrentApplicantInfoIndex'],
              target: WizardStep.ApplicantInfoStep,
            },
            {
              target: WizardStep.StatusStep,
              actions: [saveWizardDataAction],
            },
          ],
        },
      },
      [WizardStep.CancelApplicationStep]: {
        on: {
          APPLICATION_CANCELLED: { actions: ['onCancelledApplication'] },
        },
      },
      [WizardStep.ReturnApplicationStep]: {
        on: {
          APPLICATION_RETURNED: {
            actions: ['onReturnedApplication'],
          },
        },
      },
      [WizardStep.SendStep]: {
        entry: ['removeSigningMachineFromSessionStorage', 'clearSendError'],
        on: {
          SEND_SUCCESS: [
            {
              target: WizardStep.EndStep,
              actions: [
                {
                  type: 'saveIfpApplicationState',
                  params: ({ event }) => event.data,
                },
                'setShouldShowSentModal',
              ],
            },
          ],
          SEND_FAILED: [
            {
              target: WizardStep.StatusStep,
              actions: { type: 'setSendError', params: ({ event }) => event.error },
            },
          ],
          SEND_CANCELED: {
            target: WizardStep.StatusStep,
          },
        },
      },
      [WizardStep.EndStep]: {
        on: {
          CANCEL_APPLICATION: { target: WizardStep.CancelApplicationStep },
        },
      },
    },
  })
